import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useCart } from "../context/CartContext";

const CartWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-light);
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e8e8e8;
  max-width: 600px;
  margin: 0 auto;
`;

const CartSection = styled.section`
  padding: 80px 0;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const CartItems = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 30px;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 15px;
  }
`;

const ItemImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
  background: #f8f9fa;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h3 {
    font-size: 1.2rem;
    color: var(--text-dark);
    margin-bottom: 8px;
  }

  p {
    color: var(--text-light);
    font-size: 0.9rem;
    margin-bottom: 5px;
  }

  .category {
    display: inline-block;
    background: var(--secondary-color);
    color: white;
    padding: 4px 12px;
    border-radius: 15px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    margin-top: 8px;
  }
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: center;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--background-light);
  padding: 8px 12px;
  border-radius: 8px;

  button {
    background: var(--secondary-color);
    color: white;
    border: none;
    width: 28px;
    height: 28px;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
      background: #c9a660;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  span {
    min-width: 30px;
    text-align: center;
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const RemoveButton = styled.button`
  background: transparent;
  color: #ef4444;
  border: 1px solid #ef4444;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #ef4444;
    color: white;
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 30px;
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--secondary-color);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  color: var(--text-dark);

  &.total {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--primary-color);
    padding-top: 15px;
    border-top: 2px solid #e5e7eb;
    margin-top: 20px;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 15px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }
`;

const ContinueShoppingButton = styled.button`
  width: 100%;
  padding: 15px;
  background: transparent;
  color: var(--secondary-color);
  border: 2px solid var(--secondary-color);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--secondary-color);
    color: white;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  .icon {
    font-size: 4rem;
    color: var(--text-light);
    margin-bottom: 20px;
  }

  h2 {
    color: var(--text-dark);
    margin-bottom: 15px;
  }

  p {
    color: var(--text-light);
    margin-bottom: 30px;
  }
`;

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } =
    useCart();

  const handleCheckout = () => {
    // Navigate to contact page for quote
    navigate("/contact");
  };

  return (
    <CartWrapper>
      <HeroSection>
        <Container>
          <HeroTitle>Shopping Cart</HeroTitle>
          <HeroSubtitle>
            Review your selected containers and request a quote
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <CartSection>
        <Container>
          {cartItems.length === 0 ? (
            <EmptyCart>
              <div className="icon">
                <i className="fas fa-shopping-cart"></i>
              </div>
              <h2>Your cart is empty</h2>
              <p>Browse our container collection and add items to your cart</p>
              <CheckoutButton onClick={() => navigate("/containers")}>
                Browse Containers
              </CheckoutButton>
            </EmptyCart>
          ) : (
            <CartGrid>
              <CartItems>
                <h2 style={{ marginBottom: "20px", color: "var(--text-dark)" }}>
                  Cart Items ({getCartTotal()})
                </h2>
                {cartItems.map((item) => (
                  <CartItem key={item.id}>
                    <ItemImage>
                      <img src={item.image} alt={item.title} />
                    </ItemImage>
                    <ItemInfo>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <span className="category">{item.category}</span>
                    </ItemInfo>
                    <ItemActions>
                      <QuantityControl>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                        >
                          +
                        </button>
                      </QuantityControl>
                      <RemoveButton onClick={() => removeFromCart(item.id)}>
                        Remove
                      </RemoveButton>
                    </ItemActions>
                  </CartItem>
                ))}
              </CartItems>

              <CartSummary>
                <SummaryTitle>Order Summary</SummaryTitle>
                <SummaryRow>
                  <span>Total Items:</span>
                  <strong>{getCartTotal()}</strong>
                </SummaryRow>
                <SummaryRow>
                  <span>Containers:</span>
                  <strong>{cartItems.length} type(s)</strong>
                </SummaryRow>
                <SummaryRow className="total">
                  <span>Request Quote For:</span>
                  <span>{getCartTotal()} Container(s)</span>
                </SummaryRow>

                <CheckoutButton onClick={handleCheckout}>
                  Request Quote
                </CheckoutButton>
                <ContinueShoppingButton onClick={() => navigate("/containers")}>
                  Continue Shopping
                </ContinueShoppingButton>

                <div
                  style={{
                    marginTop: "20px",
                    padding: "15px",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--text-light)",
                      margin: 0,
                      textAlign: "center",
                    }}
                  >
                    <i className="fas fa-info-circle"></i> Get custom pricing
                    for your container selection
                  </p>
                </div>
              </CartSummary>
            </CartGrid>
          )}
        </Container>
      </CartSection>
    </CartWrapper>
  );
};

export default Cart;
